/**
 * dawn-cart-helpers.js
 *
 * Minimum slice of Dawn's global.js / pubsub.js / constants.js required by
 * cart.js, cart-drawer.js, and cart-notification.js.
 *
 * Provides: PUB_SUB_EVENTS, ON_CHANGE_DEBOUNCE_TIMER, subscribe, publish,
 *           debounce, fetchConfig, getFocusableElements, trapFocus,
 *           removeTrapFocus, onKeyUpEscape, CartPerformance (stub),
 *           default window.cartStrings / window.quickOrderListStrings.
 *
 * Source: https://github.com/Shopify/dawn (MIT)
 * Loaded BEFORE cart.js / cart-drawer.js / cart-notification.js in theme.liquid.
 *
 * If you ever pull Dawn's full global.js into the theme, delete this file and
 * remove the script tag from theme.liquid — Dawn provides these directly.
 */

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// ---- pub/sub ----
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }
  subscribers[eventName] = [...subscribers[eventName], callback];
  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName].map((callback) => callback(data));
    return Promise.all(promises);
  }
  return Promise.resolve();
}

// ---- misc ----
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

// ---- focus management ----
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

// ---- CartPerformance: no-op stub. Dawn ships a richer one; this just makes
// the cart code's calls into safe no-ops so we don't pull in performance.js. ----
const CartPerformance = {
  createStartingMarker(name) {
    return { name, start: performance.now() };
  },
  measure(name, fn) {
    try {
      fn();
    } catch (e) {
      console.error(e);
    }
  },
  measureFromMarker(name, marker) {
    // no-op
  },
  measureFromEvent(name, event) {
    // no-op
  },
};

// ---- string tables that cart.js reads from window.* ----
// theme.liquid can override these with translated values; defaults are English.
window.cartStrings = window.cartStrings || {
  error: 'There was an error while updating your cart. Please try again.',
  quantityError:
    'You can only add [quantity] of this item to your cart.',
};

window.quickOrderListStrings = window.quickOrderListStrings || {
  min_error: 'You cannot order less than [min] of this item.',
  max_error: 'You cannot order more than [max] of this item.',
  step_error: 'You can only order this item in multiples of [step].',
};
